import React from 'react';
import { Avatar, Box, Button, Card, CardActionArea, Chip, Typography } from '@mui/material';
import { ArrowForward, LocationOn, Phone, Storefront, SwapHoriz } from '@mui/icons-material';
import { colors } from '../../theme/theme';
import { locationKeyFor } from '../LocationSwitcher';
import { MyLocation, branchLabel } from '../../hooks/useMyLocations';

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  STUDIO PROFILE FOR SOMEONE WHO RUNS SEVERAL LOCATIONS
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * A studio profile — its details, settings, holidays, announcements, gallery —
 * belongs to ONE location. Someone holding several used to land on whichever
 * one they had signed in as, with nothing saying which. So:
 *
 *   viewing "All locations"  → LocationProfileLanding: choose one
 *   viewing one location     → LocationProfileBar above the profile: which
 *                              one this is, and one click to another
 *
 * Opening a location's profile selects that location for the whole panel —
 * the same as the location switcher — because every save on the profile is a
 * write to one location, and the panel sends the selected one with each
 * request. It reloads for the same reason the switcher does.
 *
 * Only ever rendered for a multi-location login; single-location studios never
 * see either piece.
 */

export function openLocationProfile(studioId: string): void {
  try {
    localStorage.setItem(locationKeyFor(), studioId);
  } catch {
    /* storage disabled — the reload below still lands on the profile */
  }
  window.location.assign(`/studios/${studioId}`);
}

const ROLE_LABEL: Record<string, string> = {
  brand_owner: 'Brand owner',
  studio_owner: 'Owner',
  manager: 'Manager',
};

const isActiveLocation = (l: MyLocation) => l.isActive !== false && l.branchIsActive !== false;

/** "Choose a location" — shown instead of a profile while viewing All. */
export const LocationProfileLanding: React.FC<{
  locations: MyLocation[];
  roleByStudio: Record<string, string> | null;
  brandName?: string | null;
}> = ({ locations, roleByStudio, brandName }) => (
  <Box>
    <Box mb={3}>
      <Typography variant="h4" fontWeight={700}>
        {brandName ? `${brandName} locations` : 'Your locations'}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5, maxWidth: 640 }}>
        Each location has its own profile, settings, holidays and announcements. Choose the one you want to manage.
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        Opening a location also switches the panel to it. Use the location menu at the top to go back to all locations.
      </Typography>
    </Box>

    <Box display="grid" gap={2.5} sx={{ gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' } }}>
      {locations.map((l) => {
        const role = roleByStudio?.[l._id];
        const active = isActiveLocation(l);
        const cover = l.images?.[0];
        const place = [l.address, l.city].filter(Boolean).join(', ');
        return (
          <Card
            key={l._id}
            elevation={0}
            sx={{
              borderRadius: 3,
              border: `1px solid ${colors.divider}`,
              transition: 'box-shadow .2s, transform .2s, border-color .2s',
              '&:hover': { boxShadow: '0 10px 28px rgba(0,0,0,0.08)', transform: 'translateY(-2px)', borderColor: colors.primary },
            }}
          >
            <CardActionArea onClick={() => openLocationProfile(l._id)} sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
              <Box
                sx={{
                  height: 112,
                  background: cover
                    ? `center / cover no-repeat url("${cover}")`
                    : `linear-gradient(135deg, ${colors.primary}33, ${colors.primary}0D)`,
                  position: 'relative',
                }}
              >
                <Box position="absolute" top={12} right={12} display="flex" gap={0.75}>
                  {role && ROLE_LABEL[role] && (
                    <Chip size="small" label={ROLE_LABEL[role]} sx={{ bgcolor: 'rgba(255,255,255,0.92)', fontWeight: 600 }} />
                  )}
                  {!active && <Chip size="small" label="Inactive" color="default" sx={{ bgcolor: 'rgba(255,255,255,0.92)' }} />}
                </Box>
                <Avatar
                  sx={{
                    position: 'absolute', left: 16, bottom: -24, width: 48, height: 48,
                    bgcolor: colors.primary, color: '#fff', fontWeight: 700, border: '3px solid #fff',
                  }}
                >
                  {branchLabel(l).charAt(0).toUpperCase() || <Storefront />}
                </Avatar>
              </Box>
              <Box p={2} pt={4} flex={1} display="flex" flexDirection="column" gap={0.75}>
                <Typography variant="h6" fontWeight={700} noWrap>{branchLabel(l)}</Typography>
                {l.branchName && l.studioName !== l.branchName && (
                  <Typography variant="body2" color="text.secondary" noWrap>{l.studioName}</Typography>
                )}
                {place && (
                  <Box display="flex" alignItems="center" gap={0.75} color="text.secondary">
                    <LocationOn sx={{ fontSize: 16 }} />
                    <Typography variant="body2" noWrap>{place}</Typography>
                  </Box>
                )}
                {l.phoneNumber && (
                  <Box display="flex" alignItems="center" gap={0.75} color="text.secondary">
                    <Phone sx={{ fontSize: 16 }} />
                    <Typography variant="body2" noWrap>{l.phoneNumber}</Typography>
                  </Box>
                )}
                <Box display="flex" alignItems="center" gap={0.5} mt="auto" pt={1} color={colors.primary}>
                  <Typography variant="body2" fontWeight={700}>Manage profile</Typography>
                  <ArrowForward sx={{ fontSize: 18 }} />
                </Box>
              </Box>
            </CardActionArea>
          </Card>
        );
      })}
    </Box>
  </Box>
);

/** Above a location's profile: which location this is, and its siblings one click away. */
export const LocationProfileBar: React.FC<{
  current: MyLocation | undefined;
  currentName: string;
  locations: MyLocation[];
}> = ({ current, currentName, locations }) => {
  const others = locations.filter((l) => l._id !== current?._id);
  return (
    <Box
      sx={{
        mb: 3, px: 2, py: 1.5, borderRadius: 3,
        bgcolor: `${colors.primary}10`, border: `1px solid ${colors.primary}40`,
        display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
      }}
    >
      <Box display="flex" alignItems="center" gap={1.25} flex={1} minWidth={240}>
        <LocationOn sx={{ color: colors.primary }} />
        <Box>
          <Typography variant="body2" fontWeight={700}>
            You are managing {currentName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Details, settings, holidays and announcements here apply to this location only.
          </Typography>
        </Box>
      </Box>
      {others.length > 0 && (
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <SwapHoriz sx={{ fontSize: 16 }} /> Switch to
          </Typography>
          {others.map((l) => (
            <Button
              key={l._id}
              size="small"
              variant="outlined"
              onClick={() => openLocationProfile(l._id)}
              sx={{ borderRadius: 999, textTransform: 'none', bgcolor: '#fff' }}
            >
              {branchLabel(l)}
            </Button>
          ))}
        </Box>
      )}
    </Box>
  );
};
